import { RRMovement } from '../../../../types/ReceivingReport';
import React, { useEffect, useState } from 'react';
import { Button, Form, Table, TableColumnType, Tooltip } from 'antd';
import { roundAndFormat } from '../../../../lib/utils';
import { ColumnsType } from 'antd/es/table';
import { IssuesCloseOutlined } from '@ant-design/icons';
import { IssueMovement } from '../../../../types/MaterialRequest';
import { useAuthStore } from '../../../../store';
import { useReceivingReportStore } from '../../../../store/ReceivingReportStore';
import ModalForm from '../../../../components/ui/ModalForm';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';
import { BaseModel, FormField, PageableResponse } from '../../../../types';
import { useFetchData, useUpdateStatus } from '../../../../hooks';
import { Resource } from '../../../../lib/enums';
import { MRS_API } from '../../../../hooks/useMaterialRequestHooks';
import { RRS_API } from '../../../../hooks/useReceivingReportHooks';

interface ReceivingReportInfoViewProps {
  rrMovements: RRMovement[] | undefined;
  recordId?: string;
  statusRecord?: string;
}

const ReceivingReportInfoTable: React.FC<ReceivingReportInfoViewProps> = ({
  rrMovements,
  recordId,
  statusRecord,
}) => {
  const { user } = useAuthStore();
  const receivingReportStore = useReceivingReportStore();
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === 'RR',
  );

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const { data: uomData, isLoading: isUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/uoms', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const {
    mutate: mutate,
    isError: isUpdateError,
    error: updateError,
    isSuccess,
  } = useUpdateStatus(`${RRS_API}/${recordId}/issue`);

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isUpdateError) {
      setIsErrorModalOpen(true);
      setErrorMess(updateError?.response?.data.message);
    }
  }, [isSuccess, isUpdateError]);

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    setIsErrorModalOpen(false);
  };

  const columns: TableColumnType<RRMovement>[] = [
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'PRODER',
      dataIndex: 'proderNo',
      render: (_text, record) => <span>{record?.proderNo}</span>,
    },
    {
      title: 'Code',
      dataIndex: 'productCode',
      render: (_text, record) => <span>{record.product?.code}</span>,
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      render: (_text, record) => <span>{record.product?.description}</span>,
    },
    {
      title: 'Product UoM',
      dataIndex: 'productUoM',
      render: (_text, record) => <span>{record.productUom?.description}</span>,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (_text, record) => <span>{roundAndFormat(record.quantity)}</span>,
    },
    {
      title: 'Request UoM',
      dataIndex: 'requestUoM',
      render: (_text, record) => <span>{record.requestUom?.description}</span>,
    },
    {
      title: 'Received Quantity',
      dataIndex: 'requestQty',
      render: (_text, record) => (
        <span>{roundAndFormat(record.requestQty)}</span>
      ),
    },
  ];

  const [updatedColumns, setUpdatedColumns] =
    useState<ColumnsType<RRMovement>>(columns);

  useEffect(() => {
    if (user?.role === 'ROLE_ADMIN' || userModuleAccess?.approve) {
      setUpdatedColumns((prevColumns) => {
        const hasActionColumn = prevColumns.some(
          (column) => column.key === 'action',
        );
        if (!hasActionColumn) {
          return [
            ...prevColumns,
            {
              key: 'action',
              title: 'Action',
              dataIndex: 'actions',
              fixed: 'right',
              render: (_text, record) => {
                return (
                  <div className="space-x-1">
                    <Tooltip title="Issue">
                      <Button
                        type="text"
                        disabled={
                          statusRecord === 'DONE' || statusRecord === 'ISSUED'
                        }
                        icon={<IssuesCloseOutlined />}
                        size="middle"
                        onClick={() => {
                          const issueMovement: IssueMovement = {
                            product: record.product,
                            productUom: record.productUom,
                            quantity: record.quantity,
                          };
                          receivingReportStore.setReceivedMovement(
                            issueMovement,
                          );
                          setModalVisible(true);
                        }}
                      />
                    </Tooltip>
                  </div>
                );
              },
            },
          ];
        }
        return prevColumns;
      });
    }
  }, []);

  const fields: FormField[] = [
    {
      name: 'product',
      label: 'Product',
      // rules: [{ required: true, message: 'Please select product' }],
      isMultiple: false,
      disabled: true,
      type: 'input',
      defaultValue: receivingReportStore.receivedMovement?.product?.description,
    },
    {
      name: 'productUom',
      label: 'UoM',
      // rules: [{ required: true, message: 'Please select product uom' }],
      isMultiple: false,
      disabled: !(
        receivingReportStore.receivedMovement?.productUom === null ||
        receivingReportStore.receivedMovement?.productUom === undefined
      ),
      type: 'select',
      values: uomData?.data,
      isLoading: isUomLoading,
      rules: [{ required: true, message: 'Please select product UoM' }],
      defaultValue:
        receivingReportStore.receivedMovement?.productUom?.description,
    },
    {
      name: 'quantity',
      label: 'Quantity',
      rules: [{ required: true, message: 'Please input quantity' }],
      isMultiple: false,
      type: 'input',
      defaultValue: receivingReportStore.receivedMovement?.quantity,
    },
  ];

  const onFinish = (value: any) => {
    const data = receivingReportStore.receivedMovement;
    const productUom: BaseModel | undefined = uomData?.data?.find((obj) => {
      return obj.id == value.productUom;
    });
    if (data !== null) {
      data.quantity = value.quantity;
      mutate({
        id: recordId,
        form: data,
      });
    }
  };

  return (
    <>
      <Table
        rowKey={(record) => record.id}
        dataSource={rrMovements}
        columns={updatedColumns}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />

      <ModalForm
        visible={modalVisible}
        onClose={handleCloseModal}
        formFields={fields}
        rowCol={2}
        onFinish={onFinish}
        data={receivingReportStore.receivedMovement}
        setData={receivingReportStore.setReceivedMovement}
        form={form}
      />

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleInfoModalClose}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={'Movement successfully received'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default ReceivingReportInfoTable;
